import IconNameEnum from "constants/IconNameEnum";
import React from "react";
import { Button } from "react-bootstrap";
import BaseIcon from "./BaseIcon";
import BaseSpinner from "./BaseSpinner";

interface IProps {
    id?: string;
    type: "button" | "reset" | "submit";
    disabled?: boolean;
    icon?: IconNameEnum;
    iconPos?: "before" | "after";
    iconBadge?: string | number;
    iconClassName?: string;
    iconRotate?: boolean;
    label: string;
    className?: string;
    formId?: string;
    color: "yellow" | "blue" | "red" | "green" | "light" | "grey" | "white" | "link" | "none";
    hoverTitle?: string;
    onClickHandler?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onMouseDownHandler?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    bottomLabel?: boolean;
    highlighted?: boolean;
    circled?: boolean;
    spinner?: boolean;
}

class BaseButton extends React.Component<IProps> {
    private getVariant = ():
        | "warning"
        | "primary"
        | "danger"
        | "success"
        | "light"
        | "secondary"
        | "white"
        | "link" => {
        switch (this.props.color) {
            case "yellow":
                return "warning";
            case "blue":
                return "primary";
            case "red":
                return "danger";
            case "green":
                return "success";
            case "light":
                return "light";
            case "grey":
                return "secondary";
            case "white":
            case "link":
            case "none":
                return "link";
        }
    };

    private getColor = (): string => {
        switch (this.props.color) {
            case "yellow":
                return "bg-ocq-yellow text-ocq-default";
            case "blue":
                return "bg-ocq-blue text-white";
            case "grey":
                return "bg-ocq-grey text-ocq-default";
            case "light":
                return "bg-ocq-transparent text-ocq-blue";
            case "green":
            case "red":
                return "";
            case "link":
                return "";
            case "white":
                return "text-white text-decoration-none p-0";
            case "none":
                return "text-inherit text-decoration-none p-0";
        }
    };

    private getIconColor = (): "default" | "white" => {
        switch (this.props.color) {
            case "blue":
            case "red":
            case "green":
            case "white":
                return "white";
            default:
                return "default";
        }
    };

    private onMouseDownHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.props.onMouseDownHandler) {
            this.props.onMouseDownHandler(e);
        }
    };

    private handleOnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const { onClickHandler } = this.props;
        onClickHandler && onClickHandler(event);
    };

    public render(): JSX.Element {
        const {
            id,
            type,
            disabled,
            icon,
            iconPos,
            iconBadge,
            iconClassName,
            iconRotate,
            label,
            className,
            hoverTitle,
            formId,
            circled,
            bottomLabel,
            highlighted,
            spinner,
        } = this.props;

        return (
            <Button
                id={id}
                className={[
                    this.getColor(),
                    circled ? "rounded-circle p-2 ocq-circle-button" : "",
                    bottomLabel ? "text-center" : "",
                    className,
                ].join(" ")}
                type={type}
                variant={this.getVariant()}
                form={formId ? formId : undefined}
                disabled={disabled}
                onClick={this.handleOnClick}
                onMouseDown={this.onMouseDownHandler}
                title={hoverTitle}
            >
                {icon && iconPos !== "after" ? (
                    <BaseIcon
                        className={[iconClassName, label && bottomLabel !== true ? "mr-2" : ""].join(" ")}
                        icon={icon}
                        rotate={iconRotate}
                        color={this.getIconColor()}
                        badge={iconBadge}
                    />
                ) : null}
                {spinner ? <BaseSpinner as="span" animation="border" size="sm" className="mr-2" /> : null}
                {bottomLabel ? <span className="clearfix"></span> : null}
                {label ? (
                    <span
                        className={[
                            "align-middle",
                            bottomLabel ? "small d-none d-lg-inline-block" : "",
                            highlighted ? "font-weight-bolder text-ocq-blue" : "",
                        ].join(" ")}
                    >
                        {label}
                    </span>
                ) : null}
                {icon && iconPos === "after" ? (
                    <BaseIcon
                        className={[iconClassName, label && bottomLabel !== true ? "ml-2" : ""].join(" ")}
                        icon={icon}
                        rotate={iconRotate}
                        color={this.getIconColor()}
                        badge={iconBadge}
                    />
                ) : null}
            </Button>
        );
    }
}

export { BaseButton as Unconnected };
export default BaseButton;
